import { FocusMonitor } from '@angular/cdk/a11y';
import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild, computed, effect, signal } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTable, MatTableModule } from '@angular/material/table';
import { Subject } from 'rxjs';
import { MultiLevelContextMenuItem } from '../../../common/models/multi-level-context-menu-item';
import { ContextualMenuActions } from './Models/contextual-menu-actions';
import { HeaderElement } from './Models/header-element';
import { HeaderFormDialogResult } from './Models/header-form-dialog-result';
import { HeaderFormDialogComponent } from './header-form-dialog/header-form-dialog.component';

/**
 * Composant de saisie des en-têtes d'une requête HTTP.
 * (Utilisable dans un formulaire et dans un MatFormField)
 */
@Component({
    selector: 'app-headers-form',
    imports: [MatTableModule, MatIconModule, MatMenuModule, MatButtonModule],
    templateUrl: './headers-form.component.html',
    styleUrl: './headers-form.component.scss',
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: HeadersFormComponent,
        },
    ]
})
export class HeadersFormComponent implements OnInit, OnDestroy, MatFormFieldControl<HeaderElement[]> {
    /**
     * Liste des élements du menu contextuel des variables.
     */
    @Input()
    public variableContextMenuItems: MultiLevelContextMenuItem = null;

    /**
     * Liste des actions du menu contextuel du tableau des en-têtes HTTP.
     */
    public ContextualMenuActions = ContextualMenuActions;

    /**
     * Liste des colonnes affichées dans le tableau des en-têtes HTTP.
     */
    displayedColumns: string[] = ['key', 'value'];

    /**
     * Liste des en-têtes affichées dans le tableau.
     */
    headers = signal<HeaderElement[]>([]);

    /**
     * Emet lorsque des changements surviennent dans le composant pour prevenir le MatFormField parent de déclencher le change detection.
     */
    stateChanges = new Subject<void>();

    /**
     * Associe les id des elements dont le contenu sera utilisé pour décrire ce composant à l'attribut aria.
     */
    @HostBinding('attr.aria-describedby') describedBy = '';

    /**
     * Définit les id des elements dont le contenu sera utilisé pour décrire ce composant.
     * @param ids Id des elements dont le contenu sera utilisé pour décrire ce composant.
     */
    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join('');
    }

    /**
     * Définit la valeur du contrôle.
     */
    @Input()
    set value(value: HeaderElement[]) {
        this.headers.set(value);
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du contrôle.
     */
    get value(): HeaderElement[] {
        return this.headers();
    }

    /**
     * Valeur de l'identifiant qui sera attribuée à la prochaine instance du contrôle.
     */
    static nextId = 0;

    /**
     * Définit la valeur du placeholder.
     */
    set placeholder(placeholder: string) {
        // Le placeholder n'est pas utilisé dans ce composant
    }

    /**
     * Obtient la valeur du placeholder.
     */
    get placeholder() {
        return null;
    }

    /**
     * Retourne une valeur indiquant si le contrôle est vide (absence d'en-tête)
     */
    get empty(): boolean {
        return this.headers().length !== 0;
    }

    /**
     * Retourne une valeur indiquant si le libellé doit être flottant.
     * (non géré)
     */
    get shouldLabelFloat(): boolean {
        return false;
    }

    /**
     * Obtient une valeur indiquant si ce contrôle est obligatoire.
     */
    @Input()
    get required() {
        return this._required;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est obligatoire.
     */
    set required(value: boolean) {
        this._required = value;
        this.stateChanges.next();
    }

    /**
     * Valeur indiquant si le contrôle est obligatoire.
     */
    private _required = false;

    /**
     * Obtient une valeur indiquant si ce contrôle est désactivé.
     */
    @Input()
    get disabled() {
        return this._disabled;
    }

    /**
     * Définit une valeur indiquant si ce contrôle est désactivé.
     */
    set disabled(value: boolean) {
        this._disabled = value;

        let actionColumnIndex = this.displayedColumns.findIndex((c) => c === 'actions');

        if (value === false) {
            if (actionColumnIndex === -1) {
                this.displayedColumns.push('actions');
            }
        } else {
            if (actionColumnIndex !== -1) {
                this.displayedColumns.splice(actionColumnIndex, 1);
            }
        }
    }

    /**
     * Valeur indiquant si ce contrôle est désactivé.
     */
    private _disabled = false;

    /**
     * Valeur indiquant si le contrôle possède le focus.
     */
    _focused: boolean = false;

    /**
     * Obtient une valeur indiquant si le contrôle possède le focus.
     */
    get focused() {
        return this._focused;
    }

    /**
     * Définit une valeur indiquant si le contrôle possède le focus.
     */
    set focused(value: boolean) {
        this._focused = value;
        this.onTouched(value);
    }

    /**
     * Id du contrôle.
     */
    @HostBinding()
    id = `HeadersFormComponent-${HeadersFormComponent.nextId++}`;

    /**
     * Réference à l'élément MatTable du template.
     */
    @ViewChild(MatTable, { read: ElementRef, static: true })
    table: ElementRef;

    /**
     * Constructeur.
     * @param focusMonitor : Service utilisé pour écouter les changements de focus.
     * @param dialog : Service utilisé pour ouvrir des boites de dialogue.
     * @param ngControl : Reference au contrôle dans le formulaire.
     */
    constructor(
        private focusMonitor: FocusMonitor,
        public dialog: MatDialog,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
        computed(() => console.log('headers', this.headers));
        effect(() => {
            this.onChange(this.headers());
        });
    }

    /**
     * Renseigne la valeur du contrôle.
     * (Appellé par le formulaire)
     * @param value : valeur du contrôle.
     */
    writeValue(value: HeaderElement[]): void {
        this.value = value;
    }

    /**
     * Fonction à appeler lorsque la valeur du contrôle change.
     */
    private onChange;

    /**
     * Inscrit une fonction à appeler lorsque la valeur du contrôle change.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque la valeur du contrôle change.
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Fonction à appeler lorsque le contrôle gagne le focus.
     */
    private onTouched;

    /**
     * Inscrit une fonction à appeler lorsque le contrôle gagne le focus.
     * (Appellé par le formulaire)
     * @param fn fonction à appeler lorsque le contrôle gagne le focus.
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    /**
     * Specifie l'état 'désactivé' du contrôle.
     * (Appellé par le formulaire)
     * @param value : Valeur de l'état 'désactivé'.
     */
    setDisabledState(value: boolean): void {
        this.disabled = value;
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le contrôle.
     * (Appellé par le formulaire)
     * @param event evnement clic de la souris.
     */
    onContainerClick(event: MouseEvent): void {
        // On ne fait rien de particulier lorsque l'on clique sur le tableau.
    }

    /**
     * Retourne une valeur indiquant si le contrôle est en erreur.
     */
    get errorState(): boolean {
        // On considère que ce control n'est jamais en erreur.
        return false;
    }

    /**
     * Appellé lors de l'initialisation du contrôle.
     */
    ngOnInit(): void {
        this.focusMonitor.monitor(this.table).subscribe((focused) => {
            this.focused = !!focused;
            this.stateChanges.next();
        });
    }

    /**
     * Appellé lors de la destruction du contrôle.
     */
    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.table);
        this.stateChanges.complete();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Editer' du menu contextuel.
     * @param editedHeader : Instance du header a éditer.
     */
    public onEditHeader(editedHeader: HeaderElement): void {
        console.log('edit', editedHeader);

        const dialogRef = this.dialog.open(HeaderFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Édition de l'en-tête HTTP",
                item: editedHeader,
                contextMenuItems: this.variableContextMenuItems,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: HeaderFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Mise à jour de l'en-tête HTTP.
                console.log('update', dialogResult.item);
                this.headers.update((headers) => {
                    editedHeader.key = dialogResult.item.key;
                    editedHeader.value = dialogResult.item.value;

                    return headers;
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton de création d'un header.
     */
    public onCreateHeader(): void {
        console.log('create');

        const dialogRef = this.dialog.open(HeaderFormDialogComponent, {
            maxWidth: '400px',
            data: {
                title: "Ajout d'une en-tête HTTP",
                item: null,
                contextMenuItems: this.variableContextMenuItems,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de saisie.
        dialogRef.afterClosed().subscribe((dialogResult: HeaderFormDialogResult) => {
            if (dialogResult && dialogResult.status === true) {
                // Ajout de l'en-tête HTTP.
                console.log('created', dialogResult.item);
                this.headers.update((headers) => {
                    return [...headers, dialogResult.item];
                });
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'élément 'Supprimer' du menu contextuel.
     * @param event : Instance du header a supprimer.
     */
    public onDeleteHeader(event: HeaderElement): void {
        console.log('delete', event);
        this.headers.update((headers) => headers.filter((header) => header.key !== event.key));
    }
}
